import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { authJsonHeaders, clearFakeAuthStorage, fetchCsrfToken } from '../utils/authClient';

const InmateSignUpContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const SignUpCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  width: 100%;
  max-width: 600px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  text-align: center;
  color: #cccccc;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #ffffff;
  font-weight: 500;
  font-size: 1rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00ff88;
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.3);
  }

  &::placeholder {
    color: #888888;
  }

  &.error {
    border-color: #ff4444;
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }
`;

const Select = styled.select`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00ff88;
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.3);
  }

  &.error {
    border-color: #ff4444;
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }

  option {
    background: #1a1a1a;
    color: #ffffff;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  padding: 1rem;
  border: none;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 255, 136, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid #00ff88;
  border-radius: 10px;
  padding: 1rem;
  color: #00ff88;
  text-align: center;
  margin-bottom: 1rem;
`;

const InfoBox = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #00d4ff;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const InmateSignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    inmateName: '',
    inmateId: '',
    facilityName: '',
    state: '',
    areaCode: '' // Added areaCode state
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  const relationships = [
    'Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Other Family Member', 'Attorney'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 15) {
      newErrors.password = 'Password must be at least 15 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.inmateName.trim()) {
      newErrors.inmateName = 'Inmate name is required';
    }

    if (!formData.inmateId.trim()) {
      newErrors.inmateId = 'Inmate ID is required';
    }

    if (!formData.facilityName.trim()) {
      newErrors.facilityName = 'Facility name is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }



    if (!formData.areaCode) {
      newErrors.areaCode = 'Area code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      clearFakeAuthStorage();
      const csrfToken = await fetchCsrfToken();
      const response = await fetch('/api/auth/register-pending', {
        method: 'POST',
        credentials: 'include',
        headers: authJsonHeaders(csrfToken),
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          inmateName: formData.inmateName,
          inmateId: formData.inmateId,
          facilityName: formData.facilityName,
          state: formData.state,
          relationship: formData.relationship || 'Friend'
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setErrors({ submit: data.message || 'Unable to complete registration.' });
        return;
      }
      navigate('/phone-selection', {
        state: {
          inmateData: {
            inmateConnectionId: data.inmateConnectionId,
            inmateUuid: data.inmateId,
            inmateId: formData.inmateId,
            inmateName: formData.inmateName,
            facilityName: formData.facilityName,
            state: formData.state,
            areaCode: formData.areaCode
          }
        }
      });
    } catch (error) {
      setErrors({ submit: 'Unable to complete registration. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <InmateSignUpContainer>
      <SignUpCard>
        <Title>Create Inmate Account</Title>
        <Subtitle>Set up a Skylink account for an inmate</Subtitle>

        <InfoBox>
          <strong>Important:</strong> You will need the inmate's name, ID number, facility name, and state to create their account. 
          Once created, the inmate will receive a unique Skylink phone number for communication.
        </InfoBox>

        {success && (
          <SuccessMessage>
            Inmate account created successfully! A Skylink number will be assigned shortly.
          </SuccessMessage>
        )}

        {errors.submit && (
          <ErrorMessage>{errors.submit}</ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <Label htmlFor="inmateName">Inmate's Full Name</Label>
              <Input
                type="text"
                id="inmateName"
                name="inmateName"
                value={formData.inmateName}
                onChange={handleChange}
                className={errors.inmateName ? 'error' : ''}
                placeholder="Enter inmate's full name"
              />
              {errors.inmateName && <ErrorMessage>{errors.inmateName}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="inmateId">Inmate ID Number</Label>
              <Input
                type="text"
                id="inmateId"
                name="inmateId"
                value={formData.inmateId}
                onChange={handleChange}
                className={errors.inmateId ? 'error' : ''}
                placeholder="Enter inmate ID number"
              />
              {errors.inmateId && <ErrorMessage>{errors.inmateId}</ErrorMessage>}
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label htmlFor="facilityName">Facility Name</Label>
              <Input
                type="text"
                id="facilityName"
                name="facilityName"
                value={formData.facilityName}
                onChange={handleChange}
                className={errors.facilityName ? 'error' : ''}
                placeholder="Enter correctional facility name"
              />
              {errors.facilityName && <ErrorMessage>{errors.facilityName}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="state">State</Label>
              <Select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={errors.state ? 'error' : ''}
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Select>
              {errors.state && <ErrorMessage>{errors.state}</ErrorMessage>}
            </FormGroup>
          </FormRow>



          <FormRow>
            <FormGroup>
              <Label htmlFor="username">Username</Label>
              <Input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={errors.username ? 'error' : ''}
                placeholder="Choose a username for account access"
              />
              {errors.username && <ErrorMessage>{errors.username}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Create a password"
              />
              {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Confirm your password"
            />
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="areaCode">Desired Area Code</Label>
            <Input
              type="text"
              id="areaCode"
              name="areaCode"
              value={formData.areaCode}
              onChange={handleChange}
              className={errors.areaCode ? 'error' : ''}
              placeholder="Enter desired area code (e.g., 555)"
            />
            {errors.areaCode && <ErrorMessage>{errors.areaCode}</ErrorMessage>}
          </FormGroup>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Continuing...' : 'Continue to Phone Selection'}
          </SubmitButton>
        </Form>
      </SignUpCard>
    </InmateSignUpContainer>
  );
};

export default InmateSignUp; 